import { ignoreAbortError, isAbortError } from '@/utils/abort-util'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type { GetCurrentUserWorkspaceMembershipsFn } from '@/features/iam'
import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { InventorySearchViewModel } from './InventorySearchViewModel'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { InventoryTableColumns } from './inventory-table-columns'
import { type InventoryItem } from '@/features/inventory/InventoryItem'
import { CreateInventoryItemDialogViewModel } from '@/features/inventory/admin/CreateInventoryItemDialog/CreateInventoryItemDialogViewModel'
import { type InventoryStore } from '@/features/inventory/InventoryStore'
import { ArchiveItemDialogViewModel } from '@/features/inventory/admin/ArchiveItemDialog/ArchiveItemDialogViewModel'
import { EditInventoryItemDialogViewModel } from '@/features/inventory/admin/EditInventoryItemDialog/EditInventoryItemDialogViewModel'
import { WorkspaceSearchViewModel } from './WorkspaceSearchViewModel'
import { WorkspaceSelectorViewModel } from './WorkspaceSelectorViewModel'
import { CreateWorkspaceInventoryItemDialogViewModel } from '@/features/inventory/admin/CreateWorkspaceInventoryItemDialog/CreateWorkspaceInventoryItemDialogViewModel'
import { OverrideInventoryItemDialogViewModel } from '@/features/inventory/admin/OverrideInventoryItemDialog/OverrideInventoryItemDialogViewModel'
import { type FilterStorageFactory } from '@/infra/FiltersViewModel'
import type { FetchDocumentTypesFn } from '@/features/documents'
import type { DocumentType } from '@/features/documents/DocumentType'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { UnarchiveItemDialogViewModel } from '@/features/inventory/admin/UnarchiveItemDialog/UnarchiveItemDialogViewModel'
import { ViewModelList } from '@/infra/view-model-list'
import { InventoryItemRowViewModel } from './InventoryItemRowViewModel'

/**
 * Workspace filter for Inventory Items.
 */
export type InventoryManagerWorkspaceFilter = WorkspaceItems | Global

/**
 * Global Inventory Items
 */
export interface Global {
  type: 'Global'
}

/**
 * Inventory Items in a workspace.
 */
export interface WorkspaceItems {
  type: 'Workspace'
  workspace: Workspace
}

/**
 * View model for the service manager page.
 */
export class InventoryManagerViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  private readonly _itemVms = new ViewModelList({
    models: () => this.searcher.serviceItems,
    create: (item: InventoryItem) =>
      new InventoryItemRowViewModel(
        item,
        {
          isGlobalSelected: () => this.globalSelected,
          canManageWorkspaceInventory: () => this.canManageWorkspaceInventory,
          hasWorkspace: () => Boolean(this.searcher.workspace),
        },
        {
          edit: (i) => this.editInventoryItemVm.open(i),
          override: (i) => this.overrideInventoryItemVm.open(i),
          archive: (i) => void this.archiveItemVm.prompt(i),
          unarchive: (i) => void this.unarchiveItemVm.prompt(i),
        },
      ),
  })

  /**
   * Columns to populate the inventory table header and rows with.
   */
  readonly columns = InventoryTableColumns

  /**
   * Workspace filter.
   */
  @observable accessor workspaceFilter: InventoryManagerWorkspaceFilter = {
    type: 'Workspace',
    workspace: Workspace.none,
  }

  readonly searcher: InventorySearchViewModel

  readonly workspaceSelectorVm: WorkspaceSelectorViewModel

  readonly createInventoryItemVm: CreateInventoryItemDialogViewModel

  readonly createWorkspaceInventoryItemVm: CreateWorkspaceInventoryItemDialogViewModel

  readonly editInventoryItemVm: EditInventoryItemDialogViewModel

  readonly overrideInventoryItemVm: OverrideInventoryItemDialogViewModel

  readonly archiveItemVm: ArchiveItemDialogViewModel

  readonly unarchiveItemVm: UnarchiveItemDialogViewModel

  readonly workspaceSearcher: WorkspaceSearchViewModel

  @observable private accessor _documentTypes: DocumentType[] = []

  constructor(
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _inventoryStore: InventoryStore,
    private readonly _getWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _createFilterStorage: FilterStorageFactory,
    private readonly _fetchDocumentTypes: FetchDocumentTypesFn,
  ) {
    this.searcher = new InventorySearchViewModel(
      this._inventoryStore,
      this._createAlertRetryWrapper,
      this._getWorkspaceMemberships,
      this._createFilterStorage('InventoryManagement'),
    )

    this.workspaceSelectorVm = new WorkspaceSelectorViewModel(
      this._getWorkspaceMemberships,
    )

    this.createInventoryItemVm = new CreateInventoryItemDialogViewModel(
      this._inventoryStore,
      this.refreshItems,
      this._newAlert,
      this._createAlertErrorHandler,
      () => this._documentTypes,
    )

    this.createWorkspaceInventoryItemVm =
      new CreateWorkspaceInventoryItemDialogViewModel(
        this._inventoryStore,
        this._refreshWorkspacesAndItems,
        () => this.searcher.workspace,
        this.workspaceSelectorVm,
        this._newAlert,
        this._createAlertErrorHandler,
        () => this._documentTypes,
      )

    this.editInventoryItemVm = new EditInventoryItemDialogViewModel(
      this._inventoryStore,
      this._newAlert,
      this._createAlertErrorHandler,
      this.refreshItems,
      () => this.searcher.workspace,
      () => this._documentTypes,
    )

    this.overrideInventoryItemVm = new OverrideInventoryItemDialogViewModel(
      this._inventoryStore,
      this._newAlert,
      this._createAlertErrorHandler,
      this.refreshItems,
      () => this.searcher.workspace,
      this.workspaceSelectorVm,
      () => this._documentTypes,
    )

    this.archiveItemVm = new ArchiveItemDialogViewModel(
      this._inventoryStore,
      this._refreshWorkspacesAndItems,
      this._newAlert,
      this._createAlertErrorHandler,
    )

    this.unarchiveItemVm = new UnarchiveItemDialogViewModel(
      this._inventoryStore,
      this._refreshWorkspacesAndItems,
      this._newAlert,
      this._createAlertErrorHandler,
    )

    this.workspaceSearcher = new WorkspaceSearchViewModel(this._inventoryStore)

    this._lifecycle.manage(this.searcher)
    this._lifecycle.manage(this.workspaceSelectorVm)
    this._lifecycle.manage(this._itemVms)
  }

  /**
   * All Workspaces that have inventory items
   */
  @computed
  get workspaces() {
    return this.workspaceSearcher.filteredWorkspaces.map((x) => x.workspace)
  }

  @computed
  get canManageGlobalInventory() {
    return this._getWorkspaceMemberships().some(
      (wms) => wms.member.permissions.adminManageGlobalInventoryItems,
    )
  }

  @computed
  get canManageWorkspaceInventory() {
    return this._getWorkspaceMemberships().some(
      (wms) => wms.member.permissions.manageWorkspaceInventoryItems,
    )
  }

  @computed
  get globalInventoryCount() {
    return this._inventoryStore.totalItemCountPerWorkspace.get('global') ?? 0
  }

  @computed
  get globalSelected() {
    return this.workspaceFilter.type === 'Global'
  }

  /**
   * The workspace whose inventory is shown, or `Workspace.none` for the
   * global inventory.
   */
  @computed
  get selectedWorkspace() {
    return this.workspaceFilter.type === 'Global'
      ? Workspace.none
      : this.workspaceFilter.workspace
  }

  getWorkspaceInventoryItemsCount(workspace: Workspace) {
    return this._inventoryStore.workspacesWithInventoryItems.get(workspace.id)
      ?.total
  }

  /**
   * The table rows.
   */
  @computed
  get itemVms() {
    return this._itemVms.items
  }

  @computed
  get canSearch() {
    return this.workspaceSearcher.activeWorkspaces.length >= 5
  }

  @computed
  get workspacesUseDropdown() {
    return this.canManageGlobalInventory
      ? this.canSearch || this.workspaces.length === 1
      : this.workspaces.length > 1
  }

  @action.bound
  addGlobalInventoryItem() {
    this.createInventoryItemVm.open()
  }

  @action.bound
  addWorkspaceInventoryItem() {
    this.createWorkspaceInventoryItemVm.open()
  }

  @action.bound
  addInventoryItem() {
    if (this.workspaceFilter.type === 'Global') {
      return this.createInventoryItemVm.modal.show()
    }
    this.createWorkspaceInventoryItemVm.show()
  }

  /**
   * Selects the workspace to filter by.
   */
  @action.bound
  selectWorkspaceForFiltering(workspace: Workspace) {
    this.workspaceFilter =
      workspace === Workspace.none ||
      !this.workspaces.find((ws) => ws.id === workspace.id)
        ? { type: 'Global' }
        : { workspace, type: 'Workspace' }

    this.searcher.setWorkspace(
      workspace === Workspace.none ? undefined : workspace,
    )
  }

  /**
   * Shows the workspace's inventory and re-fetches its items.
   *
   * @param workspace
   */
  @action.bound
  async refreshItems(workspace: Workspace) {
    this.selectWorkspaceForFiltering(workspace)
    await this.searcher.refreshItems()
  }

  fetch = task(async (signal: AbortSignal) => {
    void this._fetchDocumentTypeOptions(signal)
    await this._fetchWorkspaces(signal)
    signal.throwIfAborted()

    if (this.canManageGlobalInventory) {
      this.selectWorkspaceForFiltering(Workspace.none)
    } else if (this.workspaces.length) {
      this.selectWorkspaceForFiltering(this.workspaces[0])
    }
  })

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this.fetch(this._lifecycle.signal).catch(ignoreAbortError())
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Re-fetches the workspaces with inventory items, then shows the
   * workspace's inventory.
   *
   * @param workspace
   */
  @action.bound
  private async _refreshWorkspacesAndItems(workspace: Workspace) {
    // The workspaces fetched before stay when the retry alert is dismissed.
    await this._fetchWorkspaces(this._lifecycle.signal).catch(() => undefined)
    await this.refreshItems(workspace)
  }

  /**
   * Fetches the workspaces with inventory items. When the retry alert is
   * dismissed, the workspaces fetched before are kept; only an abort rejects.
   */
  private async _fetchWorkspaces(signal: AbortSignal) {
    const withRetry = this._createAlertRetryWrapper(
      'Loading the inventory workspaces failed',
      signal,
    )
    try {
      await withRetry(() =>
        this.workspaceSearcher.refreshWorkspacesWithInventoryItems(signal),
      )
    } catch (err) {
      if (isAbortError(err)) {
        throw err
      }
    }
  }

  /**
   * Fetches the document types the item dialogs offer.
   */
  private async _fetchDocumentTypeOptions(signal: AbortSignal) {
    const withRetry = this._createAlertRetryWrapper(
      'Loading the document types failed',
      signal,
    )
    // The dialogs offer no document types when the alert is dismissed.
    const documentTypes = await withRetry(() =>
      this._fetchDocumentTypes(),
    ).catch(() => null)
    if (documentTypes) {
      runInAction(() => {
        this._documentTypes = documentTypes
      })
    }
  }
}
